// ARCA-602 (ARCA-589 WS3 control-deck restructure) — TokenBank primitive.
//
// Extracted from the former always-on MatchBottomBar (deleted in WS3) so the
// token chip is DECOUPLED from the power-up tiles and can be placed on its own
// in the under-board swap-panel: PRIMARY in the chess phase (where tokens are
// spent on empower / re-empower), SECONDARY in the word phase (where they are
// earned). The power-up tiles now live separately in PowerupTiles.
//
// ARCA-601 (WS2) clarity carries over: the bank is the empower/extend currency
// (NOT power-up currency — power-ups are free, 1 use/match). It is labeled
// "Tokens" (retiring the old "coins" aria-label) and the PRIMARY variant names
// what it funds so it no longer reads as currency for nearby power-up tiles.
// The i18n keys keep their `match.bottombar.tokens*` names (shared with WS2);
// only the rendering surface moved.
//
// POLISH-02: the `<Chip variant="gold">` body resolves to Sun Yellow via the
// `.chip-gold` recipe in pieces.css.
//
// DOM:
//   <div className="token-bank" data-token-variant={variant}>
//     <span data-token-counter className="token-bank__count"> Chip(gold) </span>
//     {variant === 'primary' ? <caption: name + funding hint> : <name only>}
//   </div>
//
// data-token-counter stays on the count wrapper so the JUICE-03 token-fly still
// lands on the chip. Exactly one TokenBank renders at a time (the active phase),
// so there is never a duplicate token-fly target.

import type { CSSProperties, JSX } from 'react'
import { useIntl } from 'react-intl'

import { Chip } from './Chip'

export type TokenBankVariant = 'primary' | 'secondary'

export interface TokenBankProps {
  readonly tokens: number
  readonly tokensCap?: number
  /**
   * 'primary'   — chess phase: chip + name + funding hint (the spend context).
   * 'secondary' — word phase: chip + name only, de-emphasized (the earn context).
   */
  readonly variant?: TokenBankVariant
  readonly style?: CSSProperties
}

export function TokenBank({
  tokens,
  tokensCap = 5,
  variant = 'primary',
  style,
}: TokenBankProps): JSX.Element {
  const intl = useIntl()
  // Live status label for the bank value (announced on token-fly changes).
  const tokensAria = intl.formatMessage(
    { id: 'match.bottombar.tokens' },
    { count: tokens, cap: tokensCap },
  )
  const tokensLabel = intl.formatMessage({ id: 'match.bottombar.tokens_label' })
  const tokensHint = intl.formatMessage({ id: 'match.bottombar.tokens_hint' })

  return (
    <div
      className="token-bank"
      data-screen-region="token-bank"
      data-token-variant={variant}
      style={style}
    >
      <span
        data-token-counter
        className="token-bank__count"
        style={{ display: 'inline-flex' }}
      >
        <Chip variant="gold" ariaLabel={String(tokensAria)}>
          {tokens}/{tokensCap}
        </Chip>
      </span>
      <span className="token-bank__caption">
        {/* Name duplicates the chip's "tokens" status label, so hide it from
            SR; the funding hint (primary only) carries the unique information. */}
        <span className="token-bank__name fd" aria-hidden="true">
          {tokensLabel}
        </span>
        {variant === 'primary' ? (
          <span className="token-bank__hint">{tokensHint}</span>
        ) : null}
      </span>
    </div>
  )
}
